import { toOption3 } from '@client/new/helpers/types';
import { BundleItemType, GetOfferQuery } from 'generated/graphql';
import { isNumber, isString } from './offer.utils';
import { OfferDetailsData } from '@client/pages/CustomProgramPage/OfferDetails.mappers';

export const toOfferData = (data: GetOfferQuery): OfferDetailsData => {
  type Item = NonNullable<GetOfferQuery['bundleById']['items']>[number];
  const onItems = <K extends keyof Item>(v: BundleItemType, f: K) =>
    (data.bundleById.items ?? [])
      .filter((item) => item.itemType === v)
      .map((v) => v[f]);

  const programIds = onItems(BundleItemType.Program, 'itemId').filter(isNumber);
  const programs = (data.creator?.programs ?? [])
    .filter((v) => programIds.includes(v.id))
    .map(toOption3('id', 'name'));

  const toMediaUrl = (
    media?: {
      videoThumbnailUrl?: string | null;
      cdn01?: string | null;
      image?: string | null;
      video?: string | null;
    } | null,
  ) => {
    if (!media) return '';
    const content = media.image || media.video || media.videoThumbnailUrl;
    if (!content) return '';
    if (!media.cdn01) return content;

    if (media.videoThumbnailUrl) {
      return media.videoThumbnailUrl;
    }
    const url = media.cdn01 + content;

    return encodeURI(url ?? '');
  };

  // Legacy Workouts-section fallback chain: workout cover → exercise media →
  // creator avatar, so a workout without its own media never shows blank.
  const avatarUrl = toMediaUrl(data.creator?.mediaAvatar);

  const workouts = (data.creator?.programs ?? []).flatMap((program) =>
    (program.workouts ?? [])
      .filter(
        (workout): workout is NonNullable<typeof workout> => workout != null,
      )
      .map((workout) => ({
        value: workout.id,
        label: workout.name ?? '',
        programId: workout.programId ?? null,
        programName: program.name ?? '',
        imgUrl:
          toMediaUrl(workout.mediaCover) ||
          toMediaUrl(workout.mediaExercise) ||
          avatarUrl,
      })),
  );

  const contentIds = onItems(BundleItemType.Content, 'contentId').filter(
    isString,
  );
  const contentItems = data.content.contentItems
    .filter((v) => contentIds.includes(v.value.id))
    .map((item) => ({
      value: item.value.id,
      label: (item.value as { title?: string }).title || '',
    }));

  const featureIds = onItems(BundleItemType.Feature, 'itemId').filter(isNumber);
  const features = data.feature
    .filter((v) => featureIds.includes(v.id))
    .map(toOption3('code', 'name'));

  return {
    programIds: { options: programs },
    workoutIds: { options: workouts },
    extrasIds: { options: contentItems },
    communityFeatures: { options: features },
    price: data.bundleById.oneTimeProduct?.price ?? null,
    checkoutUrl: data.bundleById.checkoutPage?.url ?? null,
  };
};
